'use client'
import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import { atom } from 'nanostores'
import React from 'react'
import { appThemeLocalStorage } from './local-storage'

export const getSystemTheme = () => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

type Theme = 'light' | 'dark' | 'system'
let initialTheme: Theme = 'light'
if (globalThis.window) {
  const storedTheme = appThemeLocalStorage.get()
  if (storedTheme === 'light' || storedTheme === 'dark') {
    initialTheme = storedTheme
  } else {
    initialTheme = 'light'
  }
}

const themeStore = atom<Theme>(initialTheme)
const applyCurrentTheme = () => {
  try {
    const themeInLocalStorage = appThemeLocalStorage.get()
    if (themeInLocalStorage === 'dark') {
      document.documentElement.classList.add('dark')
      if (themeInLocalStorage === 'dark' && themeStore.get() !== 'dark') {
        themeStore.set('dark')
      }
    } else {
      document.documentElement.classList.remove('dark')
      themeStore.set('light')
    }
    if (!themeInLocalStorage) {
      themeStore.set('light')
    }
  } catch (err) {
    console.error('Failed to apply theme', err)
  }
}

export const useTheme = () => useStore(themeStore)
export const setTheme = (theme: Theme) => {
  if (theme === 'system') {
    appThemeLocalStorage.clear()
  } else {
    appThemeLocalStorage.set(theme)
  }
  themeStore.set(theme)
  applyCurrentTheme()
}

export const useResolvedTheme = () => {
  const theme = useTheme()
  return theme === 'system' ? getSystemTheme() : theme
}

export const ThemeApplier: React.FC = () => {
  React.useLayoutEffect(() => {
    applyCurrentTheme()
  }, [])
  return null
}

export const ThemeToggler: React.FC<React.ComponentProps<'select'>> = ({ className, ...props }) => {
  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const theme = event.target.value
    setTheme(theme as Theme)
    applyCurrentTheme()
  }, [])
  const value = useTheme()
  return (
    <select {...props} value={value} onChange={handleChange} className={clsx(className, 'bg-transparent')}>
      <option value="light">Light</option>
      <option value="dark">Dark</option>
      <option value="auto">Auto</option>
    </select>
  )
}
